
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import {
  cancelContentCampaign,
  listContentCampaigns,
  getContentCampaignFanout,
  retryContentCampaign
} from "@/api/ghlContent.api.js";

export default defineComponent({
  name: "content-publish-history",
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  emits: ["edit-campaign"],
  data() {
    return {
      loading: true,
      retrying: false,
      discardingId: 0,
      campaigns: [] as any[],
      selectedCampaignId: 0,
      fanoutItems: [] as any[]
    };
  },
  computed: {
    selectedCampaign(): any {
      return this.campaigns.find((row) => row.id === this.selectedCampaignId) || null;
    }
  },
  async mounted() {
    await this.loadCampaigns();
    this.loading = false;
  },
  methods: {
    async loadCampaigns() {
      const response = await listContentCampaigns({ limit: 50, offset: 0 });
      this.campaigns = response.data.campaigns || [];
      if (this.campaigns.length && !this.selectedCampaignId) {
        await this.selectCampaign(this.campaigns[0].id);
      }
    },
    async selectCampaign(id: number) {
      this.selectedCampaignId = id;
      const response = await getContentCampaignFanout(id, { limit: 100, offset: 0 });
      this.fanoutItems = response.data.items || [];
    },
    async retrySelected() {
      if (!this.selectedCampaignId) return;
      this.retrying = true;
      try {
        const response = await retryContentCampaign(this.selectedCampaignId);
        await this.loadCampaigns();
        await this.selectCampaign(this.selectedCampaignId);
        Swal.fire({
          title: "",
          text: `Retried: posted ${response.data.postedCount}, failed ${response.data.failedCount}.`,
          icon: response.data.failedCount ? "warning" : "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Retry failed.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.retrying = false;
      }
    },
    formatScope(teamId: number | null | undefined) {
      return teamId ? "Self + team" : "Self";
    },
    formatDate(value: any) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString();
    },
    isEditable(campaign: any) {
      return (
        campaign.status === "draft"
        || campaign.status === "preparing"
        || campaign.status === "rejected"
      );
    },
    editCampaign(id: number) {
      if (this.embedded) {
        this.$emit("edit-campaign", id);
        return;
      }
      this.$router.push({
        path: "/settings/ai247-portal",
        query: { tab: "publish", campaignId: String(id) }
      });
    },
    async discardCampaign(id: number) {
      const result = await Swal.fire({
        title: "Discard draft?",
        text: "This draft will be marked as cancelled. You can't edit it again.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Discard",
        cancelButtonText: "Keep draft",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-light",
        buttonsStyling: false
      });
      if (!result.isConfirmed) {
        return;
      }

      this.discardingId = id;
      try {
        await cancelContentCampaign(id);
        if (this.selectedCampaignId === id) {
          this.selectedCampaignId = 0;
          this.fanoutItems = [];
        }
        await this.loadCampaigns();
        Swal.fire({
          title: "",
          text: "Draft discarded.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to discard draft.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      } finally {
        this.discardingId = 0;
      }
    }
  }
});
